'use client';

import React, { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useModal } from 'components/shared';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { DiagnosticLearnMoreModal } from './DiagnosticLearnMoreModal';
import { PerformanceLabModalBodyScrollLock } from './PerformanceLabModalBodyScrollLock';
import { RECOVERY_DATA } from './CollapsibleAccordionSection/data';
import type { CollapsibleItem } from './CollapsibleAccordionSection/data';
import {
  RECOVERY_TREATMENT_THUMBS,
  recoveryTreatmentThumbSrc,
} from './recoveryPageAssets';
import { RECOVERY_TREATMENT_BUY_ROUTES } from './recoveryTreatmentBuyRoutes';
import {
  useHandlePerformanceLabBuyNavigate,
  usePerformanceLabCTALabel,
} from './hooks';

import { BodyText } from './atoms';
import {
  PlContentShellCentered,
  PlSectionHeader,
  PlSectionHeading,
  PlSectionLabel,
  PlSectionRoot,
  PlTitleStack,
  SectionContent,
} from './sectionLayout';
import styles from './RecoveryTreatmentsSection.module.css';

const TREATMENT_IDS = [
  'iv-therapy',
  'hyperbaric-oxygen-therapy-hbot',
  'cryotherapy',
  'sports-massage',
] as const;

const TREATMENT_TITLES: Record<string, { key: string; fallback: string }> = {
  'hyperbaric-oxygen-therapy-hbot': {
    key: 'recoveryPage.treatments.items.hbot.title',
    fallback: 'Hyperbaric Oxygen Therapy',
  },
  cryotherapy: {
    key: 'recoveryPage.treatments.items.cryotherapy.title',
    fallback: 'Cryotherapy',
  },
  'sports-massage': {
    key: 'recoveryPage.treatments.items.sportsMassage.title',
    fallback: 'Sport Massage',
  },
  'iv-therapy': {
    key: 'recoveryPage.treatments.items.ivTherapy.title',
    fallback: 'IV Therapy',
  },
};

const TREATMENT_IMAGE_ALTS: Record<string, { key: string; fallback: string }> =
  {
    'hyperbaric-oxygen-therapy-hbot': {
      key: 'recoveryPage.treatments.items.hbot.imageAlt',
      fallback: 'Person receiving hyperbaric oxygen therapy',
    },
    cryotherapy: {
      key: 'recoveryPage.treatments.items.cryotherapy.imageAlt',
      fallback: 'Cryotherapy chamber',
    },
    'sports-massage': {
      key: 'recoveryPage.treatments.items.sportsMassage.imageAlt',
      fallback: 'Sports massage treatment',
    },
    'iv-therapy': {
      key: 'recoveryPage.treatments.items.ivTherapy.imageAlt',
      fallback: 'IV therapy infusion',
    },
  };

const ITEMS_BY_ID = new Map(RECOVERY_DATA.items.map((item) => [item.id, item]));
const TREATMENTS: CollapsibleItem[] = TREATMENT_IDS.map((id) =>
  ITEMS_BY_ID.get(id),
).filter((item): item is CollapsibleItem => Boolean(item));

const getTreatmentImageSrc = (item: CollapsibleItem): string => {
  const thumbFilename = RECOVERY_TREATMENT_THUMBS[item.id];
  if (thumbFilename) {
    return getCompressedAssetUrl(`/${recoveryTreatmentThumbSrc(thumbFilename)}`);
  }
  return getCompressedAssetUrl(`/${item.panelImageSrc}`);
};

export const RecoveryTreatmentsSection: React.FC = () => {
  const t = useTranslations('performance-lab');
  const modal = useModal();
  const navigateToBuy = useHandlePerformanceLabBuyNavigate();
  const ctaLabel = usePerformanceLabCTALabel(
    'recoveryPage.treatments.bookCta',
    'Book now',
  );

  const handleTreatmentBookNow = useCallback(
    (treatmentId: string) => {
      const route =
        RECOVERY_TREATMENT_BUY_ROUTES[treatmentId] ?? { category: 'recovery' };
      navigateToBuy(route);
    },
    [navigateToBuy],
  );

  const openLearnMore = useCallback(
    (item: CollapsibleItem, displayTitle: string) => {
      if (!item.modalData) return;

      const thumbFilename = RECOVERY_TREATMENT_THUMBS[item.id];
      const modalData =
        item.id === 'iv-therapy' && thumbFilename
          ? {
              ...item.modalData,
              sectionImageSrc: recoveryTreatmentThumbSrc(thumbFilename),
            }
          : item.modalData;

      modal.set(
        <PerformanceLabModalBodyScrollLock>
          <DiagnosticLearnMoreModal
            title={displayTitle}
            modalData={modalData}
            onClose={() => {
              modal.hide();
              modal.clear();
            }}
            onCtaClick={() => {
              modal.hide();
              modal.clear();
              handleTreatmentBookNow(item.id);
            }}
          />
        </PerformanceLabModalBodyScrollLock>,
      );
      modal.setFullView();
      modal.setCloseBtnHidden(true);
      modal.setTop();
      modal.setAlignRight();
      modal.setPortal(true);
      modal.show();
    },
    [handleTreatmentBookNow, modal],
  );

  const rows = useMemo(
    () =>
      TREATMENTS.map((item) => {
        const titleConfig = TREATMENT_TITLES[item.id];
        const title = titleConfig
          ? t.has(titleConfig.key)
            ? t(titleConfig.key)
            : titleConfig.fallback
          : item.title;
        const altConfig = TREATMENT_IMAGE_ALTS[item.id];
        const imageAlt = altConfig
          ? t.has(altConfig.key)
            ? t(altConfig.key)
            : altConfig.fallback
          : title;
        const imageSrc = getTreatmentImageSrc(item);

        return (
          <li className={styles.row} key={item.id}>
            <div className={styles.rowInner}>
              <div className={styles.treatmentImageShell}>
                <img
                  className={styles.treatmentImage}
                  src={imageSrc}
                  alt={imageAlt}
                  loading="lazy"
                />
              </div>
              <div className={styles.titleCol}>
                <h3 className={styles.title}>{title}</h3>
              </div>
              <div className={styles.rightCol}>
                <p className={styles.description}>{item.description}</p>
                <div className={styles.actions}>
                  <button
                    className={styles.bookNowButton}
                    type="button"
                    onClick={() => handleTreatmentBookNow(item.id)}
                    aria-label={t('recoveryPage.treatments.bookCtaAria', {
                      treatment: title,
                    })}
                    data-buttontype="performance_lab_recovery_treatment_book_now"
                  >
                    {ctaLabel}
                  </button>
                  {item.modalData && (
                    <button
                      className={styles.learnMoreButton}
                      type="button"
                      onClick={() => openLearnMore(item, title)}
                      aria-label={t('recoveryPage.treatments.learnMoreAria', {
                        treatment: title,
                      })}
                    >
                      {t('recoveryPage.treatments.learnMore')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </li>
        );
      }),
    [ctaLabel, handleTreatmentBookNow, openLearnMore, t],
  );

  return (
    <PlSectionRoot
      className={styles.sectionRoot}
      aria-label={t('recoveryPage.treatments.aria')}
    >
      <SectionContent>
        <PlContentShellCentered className={styles.shell}>
          <PlSectionHeader className={styles.sectionHeader}>
            <PlSectionLabel className={styles.themedSectionLabel}>
              {t('recoveryPage.treatments.label')}
            </PlSectionLabel>
            <PlTitleStack>
              <PlSectionHeading className={styles.themedSectionHeading} $align="center">
                {t('recoveryPage.treatments.title')}
              </PlSectionHeading>
            </PlTitleStack>
            <BodyText className={styles.themedBodyText} $maxWidth="640px">
              {t('recoveryPage.treatments.subtitle')}
            </BodyText>
          </PlSectionHeader>
          <ul className={styles.list}>{rows}</ul>
        </PlContentShellCentered>
      </SectionContent>
    </PlSectionRoot>
  );
};
